import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { terminalBus } from '../lib/terminalBus'

type ChartPeriod   = '1d' | '5d' | '1mo' | '3mo' | '6mo' | '1y' | '2y' | '5y'
type ChartInterval = '1m' | '5m' | '15m' | '30m' | '1h' | '1d' | '1wk' | '1mo'
type ChartType     = 'candlestick' | 'line' | 'area' | 'heikin_ashi' | 'bars'
export type DrawMode = 'off' | 'hline' | 'trendline' | 'fibonacci'

export interface Drawing {
  id:      string
  type:    'hline' | 'trendline' | 'fibonacci'
  symbol:  string
  color:   string
  // hline
  price?:  number
  // trendline / fibonacci anchors
  time1?:  number | string
  price1?: number
  time2?:  number | string
  price2?: number
}

const DEFAULT_PANEL_ORDER = ['technical', 'analyst', 'fundamentals', 'news', 'order', 'alert', 'shortinterest', 'patterns', 'multitf']

interface TerminalState {
  activeSymbol:   string
  overlaySymbols: string[]
  chartPeriod:    ChartPeriod
  chartInterval:  ChartInterval
  chartType:      ChartType
  showVolume:     boolean
  showRsi:        boolean
  showMacd:       boolean
  showBollinger:  boolean
  showVwap:            boolean
  showVolumeProfile:      boolean
  showPrePost:            boolean
  showRelativeStrength:   boolean
  showEma9:    boolean
  showEma21:   boolean
  showEma50:   boolean
  showEma200:  boolean
  showSma50:   boolean
  showSma200:  boolean
  showAtr:           boolean
  showStoch:         boolean
  showObv:           boolean
  showPatterns:      boolean
  showEarningsCone:  boolean
  showIchimoku:      boolean
  showParabolicSar:  boolean
  showWilliamsR:     boolean
  // ── New overlays ──────────────────────────────────────
  showKeltner:    boolean
  showDonchian:   boolean
  showSupertrend: boolean
  showHma:        boolean
  showDema:       boolean
  showTema:       boolean
  // ── New sub-charts ────────────────────────────────────
  showStochRsi:   boolean
  showCci:        boolean
  showAdx:        boolean
  showRoc:        boolean
  showMfi:        boolean
  showCmf:        boolean
  // ── Auto Analysis ─────────────────────────────────────
  showAutoTrendlines: boolean
  showAutoFib:        boolean
  showAutoSR:         boolean
  showBreakouts:      boolean
  // ── Visual Backtesting ────────────────────────────────
  backtestTrades: { date: string; type: 'buy' | 'sell'; price: number }[] | null
  setBacktestTrades: (trades: { date: string; type: 'buy' | 'sell'; price: number }[] | null) => void
  aiProvider:     'claude' | 'gemini' | 'openai'

  // Drawing tools
  drawMode:  DrawMode
  drawings:  Drawing[]

  // Panel layout
  panelOrder:   string[]
  hiddenPanels: string[]

  // Theme
  theme: 'dark' | 'light'
  setTheme: (t: 'dark' | 'light') => void

  // Real-time data toggle
  liveData: boolean
  setLiveData: (v: boolean) => void

  // Hotkey modal
  hotkeyModalOpen: boolean
  setHotkeyModalOpen: (v: boolean) => void

  // Onboarding
  onboardingDone: boolean
  setOnboardingDone: () => void

  setSymbol:       (s: string) => void
  setPeriod:       (p: ChartPeriod) => void
  setInterval:     (i: ChartInterval) => void
  setChartType:    (t: ChartType) => void
  toggleIndicator: (key: 'showVolume' | 'showRsi' | 'showMacd' | 'showBollinger' | 'showVwap' | 'showVolumeProfile' | 'showPrePost' | 'showRelativeStrength' | 'showEma9' | 'showEma21' | 'showEma50' | 'showEma200' | 'showSma50' | 'showSma200' | 'showAtr' | 'showStoch' | 'showObv' | 'showPatterns' | 'showEarningsCone' | 'showIchimoku' | 'showParabolicSar' | 'showWilliamsR' | 'showKeltner' | 'showDonchian' | 'showSupertrend' | 'showHma' | 'showDema' | 'showTema' | 'showStochRsi' | 'showCci' | 'showAdx' | 'showRoc' | 'showMfi' | 'showCmf' | 'showAutoTrendlines' | 'showAutoFib' | 'showAutoSR' | 'showBreakouts') => void
  setAiProvider:   (p: 'claude' | 'gemini' | 'openai') => void
  addOverlay:      (s: string) => void
  removeOverlay:   (s: string) => void
  clearOverlays:   () => void

  setDrawMode:   (mode: DrawMode) => void
  addDrawing:    (d: Omit<Drawing, 'id'>) => void
  removeDrawing: (id: string) => void
  clearDrawings: (symbol: string) => void

  // Comparison chart mode
  comparisonMode:    boolean
  setComparisonMode: (v: boolean) => void

  setPanelOrder:         (order: string[]) => void
  togglePanelVisibility: (panel: string) => void
}

export const useTerminalStore = create<TerminalState>()(
  persist(
    (set) => ({
      activeSymbol:   'AAPL',
      overlaySymbols: [],
      chartPeriod:    '3mo',
      chartInterval:  '1d',
      chartType:      'candlestick',
      showVolume:     true,
      showRsi:        true,
      showMacd:       false,
      showBollinger:  false,
      showVwap:            false,
      showVolumeProfile:    false,
      showPrePost:          false,
      showRelativeStrength: false,
      showEma9:    false,
      showEma21:   false,
      showEma50:   false,
      showEma200:  false,
      showSma50:   false,
      showSma200:  false,
      showAtr:           false,
      showStoch:         false,
      showObv:           false,
      showPatterns:      false,
      showEarningsCone:  false,
      showIchimoku:      false,
      showParabolicSar:  false,
      showWilliamsR:     false,
      showKeltner:    false,
      showDonchian:   false,
      showSupertrend: false,
      showHma:        false,
      showDema:       false,
      showTema:       false,
      showStochRsi:   false,
      showCci:        false,
      showAdx:        false,
      showRoc:        false,
      showMfi:        false,
      showCmf:        false,
      showAutoTrendlines: false,
      showAutoFib:        false,
      showAutoSR:         false,
      showBreakouts:      false,
      backtestTrades: null,
      aiProvider:     'claude',

      drawMode: 'off',
      drawings: [],

      comparisonMode: false,
      setComparisonMode: (comparisonMode) => set({ comparisonMode }),

      panelOrder:   [...DEFAULT_PANEL_ORDER],
      hiddenPanels: [],

      theme: 'dark',
      setTheme: (theme) => {
        document.documentElement.classList.toggle('light', theme === 'light')
        set({ theme })
        // Persist to Supabase user metadata (fire-and-forget, silently no-ops if not logged in)
        import('../lib/supabase').then(({ supabase }) => {
          supabase.auth.updateUser({ data: { theme } }).catch(() => {})
        })
      },

      liveData: true,
      setLiveData: (liveData) => set({ liveData }),

      hotkeyModalOpen: false,
      setHotkeyModalOpen: (hotkeyModalOpen) => set({ hotkeyModalOpen }),

      onboardingDone: false,
      setOnboardingDone: () => set({ onboardingDone: true }),

      setSymbol: (s) => {
        // Reject anything that couldn't be a real ticker/symbol.
        // Allow up to 15 chars to accommodate international symbols (e.g. TATAMOTORS.NS = 13 chars).
        // Pure-alpha symbols capped at 12 chars — prevents accidental full company name input.
        const clean = s.trim().toUpperCase()
        const formatOk    = /^[A-Z0-9^][A-Z0-9.\-^=]{0,14}$/.test(clean)
        const isPureAlpha = /^[A-Z]+$/.test(clean)
        const lengthOk    = isPureAlpha ? clean.length <= 12 : true
        if (formatOk && lengthOk) {
          set({ activeSymbol: clean })
          terminalBus.publish({ type: 'SYMBOL_CHANGE', symbol: clean })
        }
      },
      setPeriod: (chartPeriod) => {
        set({ chartPeriod })
        terminalBus.publish({ type: 'PERIOD_CHANGE', period: chartPeriod })
      },
      setInterval: (chartInterval) => {
        set({ chartInterval })
        terminalBus.publish({ type: 'INTERVAL_CHANGE', interval: chartInterval })
      },
      setChartType:      (chartType)     => set({ chartType }),
      setBacktestTrades: (backtestTrades) => set({ backtestTrades }),
      setAiProvider:     (aiProvider)    => set({ aiProvider }),

      toggleIndicator: (key) => set((s) => ({ [key]: !s[key] })),

      addOverlay: (sym) => set((s) => {
        const upper = sym.toUpperCase()
        if (s.overlaySymbols.includes(upper) || upper === s.activeSymbol) return s
        const next = [...s.overlaySymbols, upper].slice(-2)
        return { overlaySymbols: next }
      }),
      removeOverlay: (sym) => set((s) => ({
        overlaySymbols: s.overlaySymbols.filter((o) => o !== sym.toUpperCase()),
      })),
      clearOverlays: () => set({ overlaySymbols: [] }),

      setDrawMode: (drawMode) => set({ drawMode }),

      addDrawing: (d) => set((s) => ({
        drawings: [...s.drawings, { ...d, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` }],
      })),

      removeDrawing: (id) => set((s) => ({
        drawings: s.drawings.filter((d) => d.id !== id),
      })),

      clearDrawings: (symbol) => set((s) => ({
        drawings: s.drawings.filter((d) => d.symbol !== symbol),
        drawMode: 'off',
      })),

      setPanelOrder: (panelOrder) => set({ panelOrder }),

      togglePanelVisibility: (panel) => set((s) => {
        const hidden = s.hiddenPanels.includes(panel)
          ? s.hiddenPanels.filter((p) => p !== panel)
          : [...s.hiddenPanels, panel]
        return { hiddenPanels: hidden }
      }),
    }),
    {
      name: 'terminal-prefs',
      // Migrate old persisted state missing new fields
      version: 14,
      migrate: (state: unknown) => {
        const s = state as Record<string, unknown>
        // Reset any invalid activeSymbol that was persisted (e.g. a company name typed directly).
        // US equity tickers are 1-5 pure-alpha chars (AAPL, GOOGL, CMCSA).
        // Longer formats only valid with special chars: BRK.B, BTC-USD, ^GSPC, MATIC-USD.
        // Reject anything that looks like a company name (pure alpha, > 5 chars).
        const sym = typeof s.activeSymbol === 'string' ? s.activeSymbol.trim().toUpperCase() : ''
        const formatOk  = /^[A-Z0-9^][A-Z0-9.\-^=]{0,14}$/.test(sym)
        const isPureAlpha = /^[A-Z]+$/.test(sym)
        const lengthOk  = isPureAlpha ? sym.length <= 12 : true
        const validSym  = (formatOk && lengthOk) ? sym : 'AAPL'
        return {
          ...s,
          activeSymbol:    validSym,
          drawMode:        'off',
          drawings:        [],
          comparisonMode:  false,
          panelOrder:      [...DEFAULT_PANEL_ORDER],
          hiddenPanels:    [],
          hotkeyModalOpen: false,
        }
      },
    }
  )
)
